import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search } from 'lucide-react';
import { PromptModal } from '../../components/PromptModal';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const METHOD_OPTIONS = ['CASH', 'BANK_TRANSFER', 'MOBILE_MONEY', 'CARD', 'OTHER'];
const METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', MOBILE_MONEY: 'Mobile Money', CARD: 'Card', OTHER: 'Other',
};
const LIMIT = 20;

const EMPTY_FORM = { shipmentId: '', amount: '', currency: 'RWF', paymentMethod: 'CASH', transactionReference: '', dueDate: '' };

export const CompanyAdminPayments: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [trackingSearch, setTrackingSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');
  const [promptAction, setPromptAction] = useState<{ id: string; type: 'fail' | 'refund' } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (trackingSearch) filters.trackingNumber = trackingSearch;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setPayments(d?.payments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, statusFilter, trackingSearch, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const complete = async (id: string) => {
    setActionId(id);
    try { await apiClient.completePayment(id, {}); toast('Payment marked complete', 'success'); load(); }
    catch (e: any) { toast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setActionId(''); }
  };

  const handlePromptConfirm = async (reason: string) => {
    if (!promptAction) return;
    const { id, type } = promptAction;
    setPromptAction(null);
    setActionId(id);
    try {
      if (type === 'fail') await apiClient.failPayment(id, reason);
      else await apiClient.refundPayment(id, reason);
      toast(`Payment ${type === 'fail' ? 'failed' : 'refunded'}`, 'success');
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setActionId(''); }
  };

  const createPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload: any = { ...form, amount: parseFloat(form.amount) };
      if (!payload.transactionReference) delete payload.transactionReference;
      if (!payload.dueDate) delete payload.dueDate;
      await apiClient.createPayment(payload);
      toast('Payment created', 'success');
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed to create payment', 'error'); }
    finally { setCreating(false); }
  };

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      {promptAction && (
        <PromptModal
          title={promptAction.type === 'fail' ? 'Fail Payment' : 'Refund Payment'}
          label={`Reason for ${promptAction.type === 'fail' ? 'failing' : 'refunding'} this payment`}
          placeholder="Enter reason..."
          required
          onConfirm={handlePromptConfirm}
          onCancel={() => setPromptAction(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">Payments</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(v => !v)}>
          <Plus size={15} /> New Payment
        </button>
      </div>

      {/* Create payment form */}
      {showCreate && (
        <div className="card" style={{ marginBottom: 20, maxWidth: 700 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>New Payment</h2>
          <form onSubmit={createPayment}>
            <div className="form-grid">
              <div className="form-group full">
                <label>Shipment ID *</label>
                <input required value={form.shipmentId} onChange={e => set('shipmentId', e.target.value)} placeholder="Paste shipment UUID" />
              </div>
              <div className="form-group">
                <label>Amount *</label>
                <input required type="number" min="0" step="any" value={form.amount} onChange={e => set('amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <input value={form.currency} onChange={e => set('currency', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Payment Method *</label>
                <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                  {METHOD_OPTIONS.map(m => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Transaction Reference</label>
                <input value={form.transactionReference} onChange={e => set('transactionReference', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create Payment'}</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking number..." value={trackingSearch} onChange={e => { setTrackingSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 145 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 145 }} />
        </div>

        {loading ? <div className="loading-spinner">Loading payments...</div> : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Shipment</th><th>Method</th><th>Amount</th><th>Status</th><th>Reference</th><th>Due Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <tr key={p.id}>
                      <td><code style={{ fontSize: 11 }}>{p.Shipment?.trackingNumber || p.shipment?.trackingNumber || p.shipmentId?.slice(0, 8)}</code></td>
                      <td>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{Number(p.amount).toLocaleString()} {p.currency}</td>
                      <td><span className={`badge badge-${p.status?.toLowerCase()}`}>{p.status}</span></td>
                      <td style={{ fontSize: 12 }}>{p.transactionReference || '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {p.status === 'PENDING' && (
                            <>
                              <button className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionId === p.id} onClick={() => complete(p.id)}>Complete</button>
                              <button className="btn btn-danger" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionId === p.id} onClick={() => setPromptAction({ id: p.id, type: 'fail' })}>Fail</button>
                            </>
                          )}
                          {p.status === 'COMPLETED' && (
                            <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionId === p.id} onClick={() => setPromptAction({ id: p.id, type: 'refund' })}>Refund</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
